import { useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import {
    BooleanInput,
    Create,
    DateField,
    DateInput,
    Datagrid,
    DeleteButton,
    Edit,
    EditButton,
    FunctionField,
    ImageField,
    List,
    required,
    Show,
    ShowButton,
    SimpleForm,
    SimpleShowLayout,
    TextField,
    TextInput,
    useNotify,
    WrapperField,
    useListContext,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import {
    type AdminBlogRecord,
    ADMIN_BLOG_RESOURCE,
    importAdminBlogHtmlZip,
    publishAdminBlogToProduction,
} from '../admin/adminBlogDataProvider';
import { SegmentedTabs, type SegmentedTabOption } from '../admin/SegmentedTabs';
import { BlogCoverImageInput } from './BlogCoverImageInput';
import { BlogContentHtml, BlogContentInput } from './BlogContentEditor';
import { BlogTagSelectorInput } from './BlogTagSelectorInput';

const publishedStateChoices = [
    { id: '', name: 'All statuses' },
    { id: 'published', name: 'Published' },
    { id: 'draft', name: 'Draft' },
];

const publishedStateTabs: SegmentedTabOption[] = publishedStateChoices.map((choice) => ({
    value: String(choice.id),
    label: choice.name,
}));

const blogListFilters = [
    <TextInput key="search" source="search" label="Search" alwaysOn />,
    <DateInput key="dateFromUtc" source="dateFromUtc" label="Created From" />,
    <DateInput key="dateToUtc" source="dateToUtc" label="Created To" />,
];

function BlogStatusTabs() {
    const { filterValues, setFilters } = useListContext();
    const statusValue = typeof filterValues.publishedState === 'string' ? filterValues.publishedState : '';

    return (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
            <SegmentedTabs
                label="Status"
                value={statusValue}
                options={publishedStateTabs}
                onChange={(nextValue) => {
                    setFilters({ ...filterValues, publishedState: nextValue }, undefined, false);
                }}
            />
        </Box>
    );
}

function PublishedChipField() {
    const record = useRecordContext<AdminBlogRecord>();
    const isPublished = Boolean(record?.isPublished);

    return (
        <Chip
            label={isPublished ? 'Published' : 'Draft'}
            color={isPublished ? 'success' : 'default'}
            size="small"
            variant={isPublished ? 'filled' : 'outlined'}
        />
    );
}

function ExportedChipField() {
    const record = useRecordContext<AdminBlogRecord>();
    const isExported = Boolean(record?.isPublishedToTarget);

    return (
        <Chip
            label={isExported ? 'Exported' : 'Not exported'}
            color={isExported ? 'success' : 'warning'}
            size="small"
            variant={isExported ? 'filled' : 'outlined'}
        />
    );
}

function BlogPublishButton() {
    const record = useRecordContext<AdminBlogRecord>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [isPublishing, setIsPublishing] = useState(false);

    if (!record?.blogContentId) {
        return null;
    }

    const handlePublish = async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setIsPublishing(true);

        try {
            const result = await publishAdminBlogToProduction(record.blogContentId);
            notify(`Blog exported. ${result.assetCount} asset${result.assetCount === 1 ? '' : 's'} checked.`, {
                type: 'success',
            });
            refresh();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Blog export failed';
            notify(message, { type: 'error' });
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<PublishOutlinedIcon />}
            disabled={isPublishing}
            onClick={(event) => void handlePublish(event)}
            sx={{ whiteSpace: 'nowrap' }}
        >
            {isPublishing ? 'Exporting...' : record.isPublishedToTarget ? 'Republish' : 'Export'}
        </Button>
    );
}

function CoverImagePreviewField() {
    const record = useRecordContext<AdminBlogRecord>();
    const source = String(record?.coverImageUrlResolved ?? '').trim();

    if (!source) {
        return <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No cover image</Typography>;
    }

    return (
        <Box
            component="img"
            src={source}
            alt={record?.title ?? 'Blog cover'}
            sx={{
                display: 'block',
                width: '100%',
                maxWidth: 520,
                borderRadius: 4,
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
            }}
        />
    );
}

function BlogContentField() {
    const record = useRecordContext<AdminBlogRecord>();

    return (
        <Box
            sx={{
                width: '100%',
                border: '1px solid rgba(148, 163, 184, 0.24)',
                borderRadius: 4,
                p: { xs: 2, md: 3 },
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }}
        >
            <BlogContentHtml value={record?.content} />
        </Box>
    );
}

function BlogTagChipListField() {
    const record = useRecordContext<AdminBlogRecord>();
    const tags = record?.tags ?? [];

    if (tags.length === 0) {
        return <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>No tags assigned</Typography>;
    }

    return (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {tags.map((tag) => (
                <Chip key={tag.blogTagId} label={`${tag.tagTreeName}: ${tag.name}`} size="small" variant="outlined" />
            ))}
        </Stack>
    );
}

function BlogPageZipUploadInput() {
    const form = useFormContext();
    const notify = useNotify();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isImporting, setIsImporting] = useState(false);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = '';

        if (!selectedFile) {
            return;
        }

        setIsImporting(true);

        try {
            const imported = await importAdminBlogHtmlZip(selectedFile);
            const currentTitle = String(form.getValues('title') ?? '').trim();
            const currentExcerpt = String(form.getValues('excerpt') ?? '').trim();

            form.setValue('content', imported.content, { shouldDirty: true, shouldValidate: true });

            if (!currentTitle && imported.title) {
                form.setValue('title', imported.title, { shouldDirty: true, shouldValidate: true });
            }

            if (!currentExcerpt && imported.excerpt) {
                form.setValue('excerpt', imported.excerpt, { shouldDirty: true, shouldValidate: true });
            }

            notify('Blog page zip imported. Review the content before saving.', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Zip import failed';
            notify(message, { type: 'error' });
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <Alert
            severity="info"
            sx={{
                alignItems: 'center',
                borderRadius: 2,
                '& .MuiAlert-message': { width: '100%' },
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
            >
                <Box>
                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>Upload generated blog page</Typography>
                    <Typography sx={{ color: '#475569', fontSize: '0.86rem', mt: 0.25 }}>
                        Zip must include index.html with inline styles. Put images/files in assets/. JavaScript is not allowed.
                    </Typography>
                </Box>
                <input ref={inputRef} type="file" hidden accept=".zip" onChange={(event) => void handleFileChange(event)} />
                <Button
                    variant="contained"
                    startIcon={<CloudUploadOutlinedIcon />}
                    onClick={() => inputRef.current?.click()}
                    disabled={isImporting}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {isImporting ? 'Uploading...' : 'Upload Page Zip'}
                </Button>
            </Stack>
        </Alert>
    );
}

function BlogPageFormFields() {
    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <BlogPageZipUploadInput />
            <TextInput source="title" validate={required()} fullWidth />
            <TextInput source="excerpt" multiline minRows={3} fullWidth />
            <BlogCoverImageInput source="coverImageUrl" label="Cover Image" />
            <BlogTagSelectorInput source="tagIds" label="Tags" />
            <BooleanInput source="isPublished" label="Published" />
            <DateInput
                source="publishedAtUtc"
                label="Published At (UTC)"
                helperText="Leave empty to use the current UTC time when publishing."
            />
            <BlogContentInput source="content" label="Content" validate={required()} />
        </Stack>
    );
}

export const BlogPageList = () => (
    <List resource={ADMIN_BLOG_RESOURCE} filters={blogListFilters} perPage={10} sort={{ field: 'createdAt', order: 'DESC' }}>
        <BlogStatusTabs />
        <Datagrid rowClick="show" bulkActionButtons={false}>
            <TextField source="blogContentId" label="Blog Id" />
            <ImageField source="coverImageUrlResolved" label="Cover" title="title" />
            <TextField source="title" />
            <FunctionField
                label="Status"
                render={(record: AdminBlogRecord) => (
                    <Chip
                        label={record.isPublished ? 'Published' : 'Draft'}
                        color={record.isPublished ? 'success' : 'default'}
                        size="small"
                        variant={record.isPublished ? 'filled' : 'outlined'}
                    />
                )}
            />
            <FunctionField
                label="Exported"
                render={(record: AdminBlogRecord) => (
                    <Chip
                        label={record.isPublishedToTarget ? 'Exported' : 'Not exported'}
                        color={record.isPublishedToTarget ? 'success' : 'warning'}
                        size="small"
                        variant={record.isPublishedToTarget ? 'filled' : 'outlined'}
                    />
                )}
            />
            <TextField source="excerpt" />
            <FunctionField
                label="Tags"
                render={(record: AdminBlogRecord) =>
                    record.tags.length > 0 ? record.tags.map((tag) => tag.name).join(', ') : 'No tags'
                }
            />
            <DateField source="publishedAtUtc" showTime />
            <DateField source="createdAt" showTime />
            <DateField source="updatedAt" showTime />
            <WrapperField label="Export">
                <BlogPublishButton />
            </WrapperField>
            <ShowButton />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

export const BlogPageShow = () => (
    <Show resource={ADMIN_BLOG_RESOURCE}>
        <SimpleShowLayout>
            <TextField source="blogContentId" label="Blog Id" />
            <TextField source="title" />
            <WrapperField label="Status">
                <PublishedChipField />
            </WrapperField>
            <WrapperField label="Exported">
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <ExportedChipField />
                    <BlogPublishButton />
                </Stack>
            </WrapperField>
            <TextField source="excerpt" />
            <DateField source="publishedAtUtc" showTime />
            <DateField source="createdAt" showTime />
            <DateField source="updatedAt" showTime />
            <WrapperField label="Assigned Tags">
                <BlogTagChipListField />
            </WrapperField>
            <WrapperField label="Cover Image">
                <CoverImagePreviewField />
            </WrapperField>
            <WrapperField label="Content">
                <BlogContentField />
            </WrapperField>
        </SimpleShowLayout>
    </Show>
);

export const BlogPageCreate = () => (
    <Create resource={ADMIN_BLOG_RESOURCE}>
        <SimpleForm defaultValues={{ isPublished: false }}>
            <BlogPageFormFields />
        </SimpleForm>
    </Create>
);

export const BlogPageEdit = () => (
    <Edit resource={ADMIN_BLOG_RESOURCE}>
        <SimpleForm>
            <TextInput source="blogContentId" label="Blog Id" disabled fullWidth />
            <TextInput source="createdAt" label="Created At" disabled fullWidth />
            <TextInput source="updatedAt" label="Updated At" disabled fullWidth />
            <BlogPageFormFields />
        </SimpleForm>
    </Edit>
);
